/* ════════════════════════════════════════════
   TSS QUIZ — State Machine & DOM Renderer
   v4.0 — Rewritten for the v2 single-result routing
   engine per Sleepstore-Pillow-Finder-Developer-Handover.
   Four questions, every branch, deterministic first-match
   -wins rules, bundles, alternates, add-ons, conditional
   notes, cross-sell strip, URL state.
   ════════════════════════════════════════════ */

document.addEventListener('DOMContentLoaded', () => {
  const config = window.TSS_QUIZ_CONFIG;
  if (!config) return;

  const app = document.getElementById('tss-quiz-app');
  if (!app) return;

  /* ── State ──────────────────────────────── */
  let quizData = null;       // the resolved routing config (branches, skuMap, reasonLines, notes_copy, crossSellMap)
  let step = -1;              // -1 welcome, 0 purpose(q1), 1 q2, 2 q3, 3 q4, 4 lead capture, 5 results
  let purpose = null;        // 'sleep' | 'pregnancy' | 'cushion' | 'kids'
  const answers = [];        // [q2 answer label, q3 answer label, q4 answer label]
  let capturedPhone = null;
  let isTransitioning = false;

  const QUESTION_STEPS = [
    { param: 'q2', question: 'How do you usually sleep?',             hint: 'Select one' },
    { param: 'q3', question: 'What pillow feel do you prefer?',       hint: 'Select one' },
    { param: 'q4', question: 'Do you experience neck or back pain?',  hint: 'Select one' }
  ];

  /* Exact copy per section 2 of the handover doc — do not reword */
  const PURPOSE_OPTIONS = [
    { value: 'sleep',      label: 'A pillow for better sleep' },
    { value: 'pregnancy',  label: 'Full-body pregnancy support' },
    { value: 'cushion',    label: 'A cushion for office or home' },
    { value: 'kids',       label: 'A gentle pillow for kids (age 3+)' }
  ];

  /* Label <-> machine-value maps, shared across every branch (Q2-Q4 are identical everywhere) */
  const Q2_VALUES = { 'Side sleeper': 'side', 'Back sleeper': 'back', 'Stomach sleeper': 'stomach', 'I change positions during the night': 'combination' };
  const Q3_VALUES = { 'Soft and fluffy': 'soft', 'Medium and balanced': 'medium', 'Firm and supportive': 'firm', 'Adjustable depending on my mood': 'adjustable' };
  const Q4_VALUES = { 'Yes, regularly': 'regularly', 'Occasionally': 'occasionally', 'Rarely': 'rarely', 'Never': 'never' };
  const VALUE_MAPS = [Q2_VALUES, Q3_VALUES, Q4_VALUES];

  const labelForValue = (map, val) => {
    const found = Object.keys(map).find((k) => map[k] === val);
    return found || val;
  };

  /* ── SVG Icons ─────────────────────────── */
  const svgCheck = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  const svgArrowL = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>';
  const svgArrowR = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>';
  const svgEdit = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path></svg>';
  const svgQuote = '<svg width="22" height="16" viewBox="0 0 22 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 15c0-4.5 1.6-7.6 5-9"></path><path d="M13 15c0-4.5 1.6-7.6 5-9"></path></svg>';

  /* ── Helpers ────────────────────────────── */
  const formatPrice = (cents) => '\u20b9' + (cents / 100).toLocaleString('en-IN');

  /* Indian mobile numbers: 10 digits, starting 6-9. Accepts an optional +91 / 91 / 0 prefix and spaces. */
  const isValidIndianPhone = (raw) => {
    const digits = (raw || '').replace(/[\s-]/g, '').replace(/^\+?91/, '').replace(/^0/, '');
    return /^[6-9]\d{9}$/.test(digits);
  };

  /* Sends the captured number to Shopify's native contact-form notification email
     (Settings > Notifications > Contact form in the admin) — no app or API key needed.
     This notifies the STORE, it does not message the customer's WhatsApp automatically;
     that half still needs a real integration (BiteSpeed etc). */
  const submitPhoneToShopifyContact = async (phone) => {
    const purposeLabel = (PURPOSE_OPTIONS.find((o) => o.value === purpose) || {}).label || purpose || '';
    const answerSummary = [purposeLabel, answers[0], answers[1], answers[2]].filter(Boolean).join(' \u00b7 ');
    const body = 'New WhatsApp opt-in from the Pillow Quiz.\n\nPhone: ' + phone + '\nQuiz answers: ' + answerSummary + '\nPage: ' + window.location.href;

    const formData = new FormData();
    formData.append('form_type', 'contact');
    formData.append('utf8', '\u2713');
    formData.append('contact[name]', 'Pillow Quiz Lead');
    /* Shopify's contact form expects an email for its reply-to handling. No email is
       collected here, so a harmless placeholder derived from the phone digits is used
       purely to satisfy that field — it is not a real, reachable address. */
    formData.append('contact[email]', phone.replace(/\D/g, '') + '@quiz-lead.local');
    formData.append('contact[phone]', phone);
    formData.append('contact[body]', body);

    try {
      await fetch('/contact', { method: 'POST', body: formData });
    } catch (e) {
      /* Submission failed silently (e.g. offline) — sessionStorage + the custom event
         below still preserve the number so it isn't lost outright. */
    }
  };

  const fetchProduct = async (handle) => {
    if (!handle) return null;
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  };

  const findVariant = (product, variantSelectors) => {
    if (!product || !product.variants || !product.variants.length) return null;
    const keys = Object.keys(variantSelectors || {});
    if (!keys.length) return product.variants[0];
    return product.variants.find((v) => {
      return (product.options || []).every((opt, idx) => {
        const wanted = variantSelectors[opt.name];
        if (wanted === undefined) return true;
        return v['option' + (idx + 1)] === wanted;
      });
    }) || product.variants[0];
  };

  /* ── Rules engine — first-match-wins, mirrors section 3 of the handover doc ── */
  const conditionMatches = (ifObj, ans) => {
    return Object.keys(ifObj).every((key) => {
      const want = ifObj[key];
      const have = ans[key];
      if (Array.isArray(want)) return want.indexOf(have) !== -1;
      return have === want;
    });
  };

  const evaluateRules = (rulesArr, ans) => {
    for (let i = 0; i < rulesArr.length; i++) {
      const rule = rulesArr[i];
      if (conditionMatches(rule.if, ans)) {
        return rule.sku ? { type: 'sku', value: rule.sku } : { type: 'bundle', value: rule.bundle };
      }
    }
    return null;
  };

  const getBranch = (branchKey) => quizData.branches[branchKey];

  const getPrimaryResult = (branchKey, ans) => evaluateRules(getBranch(branchKey).rules, ans);

  const getAlternate = (branchKey, ans) => {
    const branch = getBranch(branchKey);
    if (!branch.alternates) return null;
    const result = evaluateRules(branch.alternates, ans);
    return result && result.type === 'sku' ? result.value : null;
  };

  const getNotes = (branchKey, ans) => {
    const branch = getBranch(branchKey);
    if (!branch.notes) return [];
    return branch.notes.filter((n) => conditionMatches(n.if, ans)).map((n) => n.note);
  };

  const getAddOns = (branchKey, ans) => {
    const branch = getBranch(branchKey);
    const list = (branch.always_add_on || []).slice();
    (branch.conditional_add_on || []).forEach((a) => {
      if (conditionMatches(a.if, ans)) list.push(a.sku);
    });
    return list;
  };

  const resolveSku = (skuKey) => (quizData.skuMap || {})[skuKey] || null;

  /* ── URL state ──────────────────────────── */
  const VALID_Q1 = PURPOSE_OPTIONS.map((o) => o.value);
  const VALID_Q234 = ['side', 'back', 'stomach', 'combination', 'soft', 'medium', 'firm', 'adjustable', 'regularly', 'occasionally', 'rarely', 'never'];

  const buildAnswersObj = () => ({
    q1: purpose,
    q2: answers[0] ? Q2_VALUES[answers[0]] : undefined,
    q3: answers[1] ? Q3_VALUES[answers[1]] : undefined,
    q4: answers[2] ? Q4_VALUES[answers[2]] : undefined
  });

  const syncUrl = () => {
    const a = buildAnswersObj();
    const params = new URLSearchParams();
    ['q1', 'q2', 'q3', 'q4'].forEach((k) => { if (a[k]) params.set(k, a[k]); });
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? '?' + qs : '');
    window.history.replaceState({}, '', newUrl);
  };

  const loadFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const q1 = params.get('q1');
    const q2 = params.get('q2');
    const q3 = params.get('q3');
    const q4 = params.get('q4');

    if (!q1) return;

    const q1Valid = VALID_Q1.indexOf(q1) !== -1;
    const restValues = [q2, q3, q4];
    const restPresent = restValues.some((v) => v !== null);
    const restValid = restValues.every((v) => v === null || VALID_Q234.indexOf(v) !== -1);

    if (!q1Valid || (restPresent && !restValid)) {
      /* Unrecognised or partially-invalid state — discard entirely, restart at Q1 */
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    purpose = q1;

    if (q2) answers[0] = labelForValue(Q2_VALUES, q2);
    if (q3) answers[1] = labelForValue(Q3_VALUES, q3);
    if (q4) answers[2] = labelForValue(Q4_VALUES, q4);

    if (q2 && q3 && q4) {
      step = 5; /* all four answered — render the result page directly */
    } else if (q2 && q3) {
      step = 3;
    } else if (q2) {
      step = 2;
    } else {
      step = 1;
    }
  };

  /* ── Init ───────────────────────────────── */
  const init = async () => {
    try {
      const res = await fetch(config.dataUrl);
      quizData = await res.json();
      loadFromUrl();
      render();
    } catch (e) {
      app.innerHTML = '<p style="text-align:center; color: var(--section-body);">Could not load quiz data. Please refresh.</p>';
    }
  };

  /* ── Transition helper ─────────────────── */
  const transitionTo = (nextStep) => {
    if (isTransitioning) return;
    isTransitioning = true;

    if (step === 0 && nextStep === 1) {
      try {
        if (!sessionStorage.getItem('tss_quiz_started')) {
          sessionStorage.setItem('tss_quiz_started', 'true');
          if (typeof window.fbq !== 'undefined') {
            window.fbq('trackCustom', 'QuizStart');
          }
        }
      } catch (e) {}
    }

    const stepEl = app.querySelector('.tss-quiz__step');
    if (stepEl) stepEl.classList.add('is-exiting');
    setTimeout(() => {
      step = nextStep;
      syncUrl();
      render();
      isTransitioning = false;
    }, 220);
  };

  /* ══════════════════════════════════════════
     MASTER RENDER
     ══════════════════════════════════════════ */
  const render = () => {
    if (!quizData) return;
    let html = (step === -1 && config.enableCinematic) ? '<div class="tss-quiz__step is-cinematic">' : '<div class="tss-quiz__step">';

    if (step === -1) html += renderWelcomeScreen();
    else if (step === 0) html += renderPurposeStep();
    else if (step >= 1 && step <= 3) html += renderQuestion();
    else if (step === 4) html += renderLeadCapture();
    else html += renderLoadingResults();

    html += '</div>';
    app.innerHTML = html;
    bindEvents();

    requestAnimationFrame(() => {
      const target = app.querySelector('.tss-quiz__question') || app.querySelector('.tss-quiz__title');
      if (target) { target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); }
    });

    if (step === 5) computeResults();
  };

  /* ══════════════════════════════════════════
     PROGRESS — always 4 nodes (Q1-Q4). Lead capture
     and results are not represented as nodes.
     ══════════════════════════════════════════ */
  const renderProgressNodes = () => {
    let html = '<div class="tss-quiz__progress">';
    for (let i = 0; i < 4; i++) {
      let cls = 'tss-quiz__progress-node';
      let inner = '';
      if (i < step) { cls += ' is-completed'; inner = svgCheck; }
      else if (i === step) { cls += ' is-active'; inner = '<span class="tss-quiz__progress-node-dot"></span>'; }
      else { inner = '&nbsp;'; }
      const clickAttr = (i < step) ? ' data-jump-to="' + i + '" style="cursor:pointer;" title="Jump back to this step"' : '';
      html += '<div class="' + cls + '"' + clickAttr + '>' + inner + '</div>';
      if (i < 3) {
        const lineCls = i < step ? 'tss-quiz__progress-line is-completed' : 'tss-quiz__progress-line';
        html += '<div class="' + lineCls + '">&nbsp;</div>';
      }
    }
    return html + '</div>';
  };

  /* ══════════════════════════════════════════
     STEP: WELCOME
     ══════════════════════════════════════════ */
  const renderWelcomeScreen = () => {
    let bgStyle = config.enableCinematic && config.cinematicImage ? `background-image: url('${config.cinematicImage}');` : '';
    let html = '<div class="' + (config.enableCinematic ? 'tss-quiz__cinematic' : 'tss-quiz__welcome') + '" style="' + bgStyle + '">';
    html += '<div class="' + (config.enableCinematic ? 'tss-quiz__cinematic-content' : 'tss-quiz__welcome-card') + '">';
    html += '<span class="tss-quiz__welcome-eyebrow">' + (config.eyebrow || 'Pillow Quiz') + '</span>';
    html += '<h2 class="tss-quiz__welcome-title">' + (config.title || 'Find your <em>perfect</em> pillow') + '</h2>';
    if (config.subtitle) html += '<p class="tss-quiz__welcome-subtext">' + config.subtitle + '</p>';
    html += '<button type="button" class="tss-btn tss-btn--primary tss-btn--block tss-quiz__start-btn" data-action="start-quiz">Start Quiz</button>';
    html += '</div></div>';
    return html;
  };

  /* ══════════════════════════════════════════
     STEP: PURPOSE (Q1)
     ══════════════════════════════════════════ */
  const renderPurposeStep = () => {
    let html = '<div class="tss-quiz__card">';
    html += renderProgressNodes();
    html += '<h3 class="tss-quiz__question">What are you shopping for?</h3>';
    html += '<p class="tss-quiz__question-hint">Select one to begin</p>';
    html += '<div class="tss-quiz__options tss-quiz__options--4-cols">';
    PURPOSE_OPTIONS.forEach((opt, index) => {
      const sel = purpose === opt.value ? ' is-selected' : '';
      const iconUrl = config.icons[opt.label] || '';
      html += '<div class="tss-quiz__option-wrapper" style="animation-delay:' + (index * 50) + 'ms">';
      html += '<button type="button" class="tss-quiz__option' + sel + '" data-purpose="' + opt.value + '">';
      html += '<div class="tss-quiz__option-icon-box">' + (iconUrl ? '<img src="' + iconUrl + '" alt="" class="tss-quiz__option-img" loading="lazy">' : '<div class="tss-quiz__option-placeholder"></div>') + '</div>';
      html += '<span class="tss-quiz__option-label">' + opt.label + '</span>';
      if (purpose === opt.value) html += '<span class="tss-quiz__option-check">' + svgCheck + '</span>';
      html += '</button></div>';
    });
    html += '</div>';
    if (config.travelLinkUrl) {
      html += '<p class="tss-quiz__travel-exit"><a href="' + config.travelLinkUrl + '">Travelling instead? Shop travel pillows \u2192</a></p>';
    }
    html += '</div>';
    return html;
  };

  /* ══════════════════════════════════════════
     STEP: QUESTION (Q2-Q4) — identical shape for every branch
     ══════════════════════════════════════════ */
  const renderQuestion = () => {
    const qIndex = step - 1;
    const s = QUESTION_STEPS[qIndex];
    const valueMap = VALUE_MAPS[qIndex];
    const options = Object.keys(valueMap);

    let html = '<div class="tss-quiz__card">';
    html += renderProgressNodes();
    html += '<h3 class="tss-quiz__question">' + s.question + '</h3>';
    html += '<p class="tss-quiz__question-hint">' + s.hint + '</p>';
    html += '<div class="tss-quiz__options">';
    options.forEach((opt, index) => {
      const sel = answers[qIndex] === opt ? ' is-selected' : '';
      const iconUrl = config.icons[opt] || '';
      html += '<div class="tss-quiz__option-wrapper" style="animation-delay:' + (index * 50) + 'ms">';
      html += '<button type="button" class="tss-quiz__option' + sel + '" data-value="' + opt + '">';
      html += '<div class="tss-quiz__option-icon-box">' + (iconUrl ? '<img src="' + iconUrl + '" alt="" class="tss-quiz__option-img" loading="lazy">' : '<div class="tss-quiz__option-placeholder"></div>') + '</div>';
      html += '<span class="tss-quiz__option-label">' + opt + '</span>';
      if (answers[qIndex] === opt) html += '<span class="tss-quiz__option-check">' + svgCheck + '</span>';
      html += '</button></div>';
    });
    html += '</div>';
    html += '<div class="tss-quiz__nav">';
    html += '<button type="button" class="tss-quiz__btn-back" data-action="back">' + svgArrowL + ' Back</button>';
    html += '<button type="button" class="tss-btn tss-quiz__btn-next" data-action="next"' + (answers[qIndex] ? '' : ' disabled') + '>Next ' + svgArrowR + '</button>';
    html += '</div></div>';
    return html;
  };

  /* ══════════════════════════════════════════
     STEP: LEAD CAPTURE (after Q4, before results — never before)
     ══════════════════════════════════════════ */
  const renderLeadCapture = () => {
    let html = '<div class="tss-quiz__card tss-quiz__card--lead">';
    html += '<div class="tss-quiz__lead-header">';
    html += '<div class="tss-quiz__lead-icon-wrapper">';
    html += config.recommendationIcon
      ? '<img src="' + config.recommendationIcon + '" alt="" class="tss-quiz__lead-icon">'
      : '<svg class="tss-quiz__lead-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"></path><path d="M12 7v14"></path></svg>';
    html += '</div>';
    html += '<h2 class="tss-quiz__title tss-quiz__title--serif">' + (config.leadTitle || 'Unlock your results') + '</h2>';
    if (config.leadSubtitle) html += '<p class="tss-quiz__subtext">' + config.leadSubtitle + '</p>';
    html += '</div>';
    html += '<div class="tss-quiz__lead-body"><div class="tss-quiz__lead-form">';
    html += '<input type="tel" class="tss-quiz__lead-input" id="tss-quiz-phone" placeholder="WhatsApp number (e.g. +91 98765 43210)" autocomplete="tel" inputmode="tel">';
    html += '<p class="tss-quiz__lead-error" id="tss-quiz-phone-error" hidden></p>';
    html += '<button type="button" class="tss-quiz__lead-btn" data-action="submit">' + (config.leadButton || 'Show My Results') + '</button>';
    html += '<button type="button" class="tss-quiz__lead-skip" data-action="skip">Skip this, just show me my match</button>';
    html += '</div></div>';
    html += '<div class="tss-quiz__lead-footer">';
    html += '<p class="tss-quiz__lead-disclaimer">' + (config.leadDisclaimer || 'Optional \u2014 we only use this to send your result.') + '</p>';
    html += '<div class="tss-quiz__lead-actions"><button type="button" class="tss-quiz__btn-back" data-action="back">' + svgArrowL + ' Back</button></div>';
    html += '</div></div>';
    return html;
  };

  const renderLoadingResults = () => {
    return '<div class="tss-quiz__skeleton-results"><div class="tss-quiz__skeleton-card">' +
      '<div class="tss-quiz__skeleton-media"></div><div class="tss-quiz__skeleton-body">' +
      '<div class="tss-quiz__skeleton-line medium"></div><div class="tss-quiz__skeleton-line short"></div>' +
      '<div class="tss-quiz__skeleton-line pill"></div></div></div></div>';
  };

  /* ══════════════════════════════════════════
     RESULT PAGE COMPONENTS
     ══════════════════════════════════════════ */
  const renderRecap = () => {
    const purposeLabel = (PURPOSE_OPTIONS.find((o) => o.value === purpose) || {}).label || '';
    const parts = [purposeLabel, answers[0], answers[1], answers[2]].filter(Boolean);
    return '<div class="tss-quiz__recap">' +
      '<span class="tss-quiz__recap-text">' + parts.join(' \u00b7 ') + '</span>' +
      '<button type="button" class="tss-quiz__recap-edit" data-action="restart-to-q1">' + svgEdit + ' Edit</button>' +
      '</div>';
  };

  const renderSingleCard = (p, skuKey, isOutOfStock, addOnProducts, addOnSkuKeys) => {
    if (!p) return '';
    const defaultVariant = findVariant(p, (resolveSku(skuKey) || {}).variant);
    const reason = (quizData.reasonLines || {})[skuKey] || '';
    const priceStr = formatPrice(defaultVariant.price);
    const compareStr = defaultVariant.compare_at_price > defaultVariant.price
      ? '<s class="tss-quiz__showcase-compare" data-compare-display>' + formatPrice(defaultVariant.compare_at_price) + '</s>' : '<s class="tss-quiz__showcase-compare" data-compare-display style="display:none;"></s>';
    const hasRealOptions = p.options && p.options.length > 0 && p.options[0].name !== 'Title';
    const variantsJson = JSON.stringify(p.variants || []).replace(/'/g, '&#39;');

    let featuredImgUrl = '';
    if (typeof p.featured_image === 'string') featuredImgUrl = p.featured_image;
    else if (p.featured_image && p.featured_image.url) featuredImgUrl = p.featured_image.url;
    else if (p.image) featuredImgUrl = typeof p.image === 'string' ? p.image : p.image.url;
    const images = Array.isArray(p.images) && p.images.length > 0 ? p.images : (featuredImgUrl ? [featuredImgUrl] : []);

    let html = '<div class="tss-quiz__hero-showcase" data-product-handle="' + p.handle + '" data-variants=\'' + variantsJson + '\'>';
    html += '<div class="tss-quiz__showcase-header">';
    html += '<div class="tss-quiz__showcase-badge"><span class="tss-quiz__showcase-badge-text">' + (isOutOfStock ? 'CURRENTLY OUT OF STOCK' : 'YOUR MATCH') + '</span></div>';
    html += '<a href="' + p.url + '" class="tss-quiz__showcase-title-link"><h3 class="tss-quiz__showcase-title">' + p.title + '</h3></a>';
    if (reason) html += '<p class="tss-quiz__showcase-subtitle">' + reason + '</p>';
    html += '</div>';
    html += '<div class="tss-quiz__showcase-body">';

    /* Gallery — Swiper when multiple images, single frame otherwise */
    html += '<div class="tss-quiz__showcase-media">';
    if (images.length > 1) {
      html += '<div class="tss-quiz-gallery-native" style="width:100%;position:relative;overflow:hidden;border-radius:var(--ss-radius-md);"><div class="tss-quiz-gallery-track" style="display:flex;overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none;">';
      images.forEach((img) => {
        const imgSrc = typeof img === 'string' ? img : (img.url || '');
        html += '<div class="tss-quiz-gallery-slide" style="flex:0 0 100%;scroll-snap-align:start;"><img src="' + imgSrc + '" loading="lazy" class="tss-quiz__showcase-img" alt="' + p.title + '"></div>';
      });
      html += '</div>';
      html += '<button class="tss-quiz-gallery-arrow prev" aria-label="Previous image"><svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"></path></svg></button>';
      html += '<button class="tss-quiz-gallery-arrow next" aria-label="Next image"><svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"></path></svg></button>';
      html += '<div class="tss-quiz-gallery-dots"></div></div>';
      html += '<div class="tss-quiz-thumbs-native" style="margin-top:12px;"><div class="tss-quiz-thumbs-track" style="display:flex;overflow-x:auto;gap:8px;scrollbar-width:none;">';
      images.forEach((img, i) => {
        const imgSrc = typeof img === 'string' ? img : (img.url || '');
        const activeStyle = i === 0 ? 'border: 2px solid var(--section-text, #1b3d58);' : 'border: 2px solid transparent;';
        html += '<div class="tss-quiz-thumb-slide" data-index="' + i + '" style="flex:0 0 calc(25% - 6px);cursor:pointer;border-radius:var(--ss-radius-sm);' + activeStyle + '"><img src="' + imgSrc + '" loading="lazy" class="tss-quiz__thumb-img" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:calc(var(--ss-radius-sm) - 2px);"></div>';
      });
      html += '</div></div>';
    } else {
      const singleImgSrc = images.length > 0 ? (typeof images[0] === 'string' ? images[0] : images[0].url) : '';
      html += '<a href="' + p.url + '" class="tss-quiz-gallery-single" style="display:block;width:100%;aspect-ratio:1/1;border-radius:var(--ss-radius-md);overflow:hidden;position:relative;">';
      if (singleImgSrc) html += '<img src="' + singleImgSrc + '" alt="' + p.title + '" loading="lazy" class="tss-quiz__showcase-img">';
      html += '</a>';
    }
    html += '</div>';

    html += '<div class="tss-quiz__showcase-details">';
    html += '<div class="tss-quiz__showcase-price-block"><span class="tss-quiz__showcase-price-label">Shop at</span>';
    html += '<span class="tss-quiz__showcase-price" data-price-display>' + priceStr + '</span>' + compareStr + '</div>';

    if (hasRealOptions) {
      const preselect = (resolveSku(skuKey) || {}).variant || {};
      html += '<div class="tss-quiz__showcase-options">';
      p.options.forEach((opt, index) => {
        html += '<div class="tss-quiz__option-group"><label class="tss-quiz__option-label">' + opt.name + '</label>';
        html += '<div class="tss-quiz__select-wrapper"><select class="tss-quiz__option-select" data-option-index="' + index + '">';
        opt.values.forEach((val) => {
          const sel = preselect[opt.name] === val ? ' selected' : '';
          html += '<option value="' + val + '"' + sel + '>' + val + '</option>';
        });
        html += '</select></div></div>';
      });
      html += '<div class="tss-quiz__option-group"><label class="tss-quiz__option-label">QTY</label>';
      html += '<div class="tss-quiz__qty"><button type="button" class="tss-quiz__qty-btn" data-action="minus">-</button>';
      html += '<input type="number" class="tss-quiz__qty-input" value="1" min="1" data-qty-input>';
      html += '<button type="button" class="tss-quiz__qty-btn" data-action="plus">+</button></div></div>';
      html += '</div>';
    } else {
      html += '<div class="tss-quiz__showcase-options"><div class="tss-quiz__option-group"><label class="tss-quiz__option-label">QTY</label>';
      html += '<div class="tss-quiz__qty"><button type="button" class="tss-quiz__qty-btn" data-action="minus">-</button>';
      html += '<input type="number" class="tss-quiz__qty-input" value="1" min="1" data-qty-input>';
      html += '<button type="button" class="tss-quiz__qty-btn" data-action="plus">+</button></div></div></div>';
    }

    if (!isOutOfStock && addOnProducts && addOnProducts.length) {
      html += '<div class="tss-quiz__addon-inline">';
      html += '<p class="tss-quiz__addon-inline-label">Goes well with</p>';
      addOnProducts.forEach((addonP, i) => {
        if (!addonP) return;
        const addonKey = addOnSkuKeys[i];
        const addonVariant = findVariant(addonP, (resolveSku(addonKey) || {}).variant);
        let addonImg = '';
        if (typeof addonP.featured_image === 'string') addonImg = addonP.featured_image;
        else if (addonP.featured_image && addonP.featured_image.url) addonImg = addonP.featured_image.url;
        else if (addonP.images && addonP.images[0]) addonImg = typeof addonP.images[0] === 'string' ? addonP.images[0] : addonP.images[0].url;
        html += '<label class="tss-quiz__addon-inline-item">';
        html += '<input type="checkbox" class="tss-quiz__addon-checkbox" data-variant-id="' + addonVariant.id + '" data-price="' + addonVariant.price + '">';
        html += '<span class="tss-quiz__addon-inline-check"></span>';
        html += '<img src="' + addonImg + '" class="tss-quiz__addon-inline-img" alt="' + addonP.title + '" loading="lazy">';
        html += '<span class="tss-quiz__addon-inline-info"><span class="tss-quiz__addon-inline-name">' + addonP.title + '</span>';
        html += '<span class="tss-quiz__addon-inline-price">+ ' + formatPrice(addonVariant.price) + '</span></span>';
        html += '</label>';
      });
      html += '</div>';
    }

    if (isOutOfStock) {
      html += '<div class="tss-quiz__oos-notice">This exact match is out of stock right now \u2014 see the alternate below.</div>';
    } else {
      html += '<div class="tss-quiz__showcase-actions">';
      html += '<button type="button" class="tss-quiz__showcase-atc" data-variant-id="' + defaultVariant.id + '" data-base-price="' + defaultVariant.price + '">ADD TO CART</button>';
      html += '<span class="tss-quiz__addon-total" data-addon-total hidden></span>';
      html += '</div>';
    }
    html += '</div></div></div>';

    /* "Why you'll love it" breakdown — every row sourced from real product data (quizData.productFeatures). */
    html += '<div class="tss-quiz__breakdown">';
    html += '<h3 class="tss-quiz__breakdown-title">Why you\u2019ll love the ' + p.title + '</h3>';
    html += '<div class="tss-quiz__breakdown-zig-zag">';

    let baseHandle = (resolveSku(skuKey) || {}).productFeaturesKey || '';
    const prodData = (quizData.productFeatures && quizData.productFeatures[baseHandle]) ? quizData.productFeatures[baseHandle] : null;
    const features = (prodData && prodData.features) ? prodData.features : [];

    features.forEach((feat, index) => {
      const isReverse = (index % 2 === 1) ? ' tss-quiz__breakdown-row--reverse' : '';
      const fImgRaw = images.length > index ? images[index] : featuredImgUrl;
      const fImg = typeof fImgRaw === 'string' ? fImgRaw : (fImgRaw && fImgRaw.url) || '';
      html += '<div class="tss-quiz__breakdown-row' + isReverse + '">';
      html += '<div class="tss-quiz__breakdown-image"><img src="' + fImg + '" class="tss-quiz__breakdown-img" alt=""></div>';
      html += '<div class="tss-quiz__breakdown-content"><h4 class="tss-quiz__breakdown-col-title">' + feat.title + '</h4>';
      html += '<p class="tss-quiz__breakdown-col-text">' + feat.text + '</p></div></div>';
    });

    html += '</div></div>';

    return html;
  };

  const renderBundleCard = (products, skuKeys) => {
    const variants = products.map((p, i) => findVariant(p, (resolveSku(skuKeys[i]) || {}).variant));
    const totalPrice = variants.reduce((sum, v) => sum + v.price, 0);
    let html = '<div class="tss-quiz__hero-showcase tss-quiz__hero-showcase--bundle">';
    html += '<div class="tss-quiz__showcase-header">';
    html += '<div class="tss-quiz__showcase-badge"><span class="tss-quiz__showcase-badge-text">YOUR MATCH \u2014 BUNDLE</span></div>';
    html += '<h3 class="tss-quiz__showcase-title">' + products.map((p) => p.title).join(' + ') + '</h3>';
    html += '</div>';
    html += '<div class="tss-quiz__bundle-items">';
    products.forEach((p, i) => {
      const reason = (quizData.reasonLines || {})[skuKeys[i]] || '';
      const img = p.featured_image || (p.images && p.images[0]) || '';
      html += '<div class="tss-quiz__bundle-item">';
      html += '<img src="' + img + '" alt="' + p.title + '" class="tss-quiz__bundle-item-img" loading="lazy">';
      html += '<div><div class="tss-quiz__bundle-item-title">' + p.title + '</div>';
      if (reason) html += '<div class="tss-quiz__bundle-item-reason">' + reason + '</div>';
      html += '</div></div>';
    });
    html += '</div>';
    html += '<div class="tss-quiz__showcase-price-block"><span class="tss-quiz__showcase-price-label">Bundle price</span>';
    html += '<span class="tss-quiz__showcase-price">' + formatPrice(totalPrice) + '</span></div>';
    const variantIds = variants.map((v) => v.id).join(',');
    html += '<div class="tss-quiz__showcase-actions"><button type="button" class="tss-quiz__showcase-atc" data-bundle-variant-ids="' + variantIds + '">ADD BOTH TO CART</button></div>';
    html += '</div>';
    return html;
  };

  const renderNote = (noteKeys) => {
    if (!noteKeys.length) return '';
    const text = noteKeys.map((k) => (quizData.notes_copy || {})[k]).filter(Boolean).join(' ');
    if (!text) return '';
    return '<div class="tss-quiz__note"><span class="tss-quiz__note-icon">' + svgQuote + '</span><p>' + text + '</p></div>';
  };

  const renderAlternateTile = (p, skuKey) => {
    if (!p) return '';
    const variant = findVariant(p, (resolveSku(skuKey) || {}).variant);
    const img = p.featured_image || (p.images && p.images[0]) || '';
    return '<div class="tss-quiz__alternate">' +
      '<p class="tss-quiz__alternate-label">Or, if you would rather</p>' +
      '<a href="' + p.url + '" class="tss-quiz__sec-card">' +
      '<div class="tss-quiz__sec-media"><img src="' + img + '" alt="' + p.title + '" loading="lazy"></div>' +
      '<div class="tss-quiz__sec-body"><h4 class="tss-quiz__sec-title">' + p.title + '</h4>' +
      '<p class="tss-quiz__sec-price">' + formatPrice(variant.price) + '</p></div></a></div>';
  };

  const renderCrossSellStrip = (products) => {
    const valid = products.filter(Boolean);
    if (!valid.length) return '';
    let html = '<div class="tss-quiz__cross-sell"><p class="tss-quiz__cross-sell-label">Also on our shelves</p><div class="tss-quiz__cross-sell-grid">';
    valid.forEach((p) => {
      const img = p.featured_image || (p.images && p.images[0]) || '';
      html += '<a href="' + p.url + '" class="tss-quiz__sec-card"><div class="tss-quiz__sec-media"><img src="' + img + '" alt="' + p.title + '" loading="lazy"></div>' +
        '<div class="tss-quiz__sec-body"><h4 class="tss-quiz__sec-title">' + p.title + '</h4>' +
        '<p class="tss-quiz__sec-price">' + formatPrice(p.price) + '</p></div></a>';
    });
    return html + '</div></div>';
  };

  /* ══════════════════════════════════════════
     COMPUTE RESULTS
     ══════════════════════════════════════════ */
  const computeResults = async () => {
    const ans = buildAnswersObj();
    const primary = getPrimaryResult(purpose, ans);

    if (!primary) {
      /* Cannot happen if config is intact (each branch ends in an empty-condition default) */
      app.querySelector('.tss-quiz__step').innerHTML =
        '<div class="tss-quiz__header"><h2 class="tss-quiz__title">No exact match found</h2></div>' +
        '<div class="tss-quiz__restart"><button type="button" class="tss-btn tss-btn--outline" data-action="restart">Retake Quiz</button></div>';
      bindRestartEvents();
      return;
    }

    const noteKeys = getNotes(purpose, ans);
    const alternateSkuKey = getAlternate(purpose, ans);
    const addOnSkuKeys = getAddOns(purpose, ans);

    await new Promise((resolve) => setTimeout(resolve, 600));

    /* Fetch add-ons first so they can render inline inside the primary card, not as a separate section */
    const addOnEntries = addOnSkuKeys.map(resolveSku);
    const addOnProducts = await Promise.all(addOnEntries.map((e) => fetchProduct(e ? e.handle : null)));

    let primaryHtml = '';
    let isBundle = primary.type === 'bundle';
    let primaryOutOfStock = false;

    if (isBundle) {
      const skuKeys = primary.value;
      const entries = skuKeys.map(resolveSku);
      const products = await Promise.all(entries.map((e) => fetchProduct(e ? e.handle : null)));
      const valid = products.filter(Boolean);
      if (valid.length === skuKeys.length) {
        primaryHtml = renderBundleCard(valid, skuKeys);
      }
    } else {
      const skuKey = primary.value;
      const entry = resolveSku(skuKey);
      const product = await fetchProduct(entry ? entry.handle : null);
      if (product) {
        const variant = findVariant(product, entry.variant);
        primaryOutOfStock = variant && variant.available === false;
        primaryHtml = renderSingleCard(product, skuKey, primaryOutOfStock, addOnProducts, addOnSkuKeys);
      }
    }

    let alternateProduct = null;
    if (alternateSkuKey) {
      const altEntry = resolveSku(alternateSkuKey);
      alternateProduct = await fetchProduct(altEntry ? altEntry.handle : null);
    }

    const crossSellKeys = quizData.cross_sell_only || [];
    const crossSellEntries = crossSellKeys.map((k) => (quizData.crossSellMap || {})[k]).filter(Boolean);
    const crossSellProducts = await Promise.all(crossSellEntries.map((e) => fetchProduct(e.handle)));

    let html = '<div class="tss-quiz__results">';
    html += renderRecap();
    html += primaryHtml;
    html += renderNote(noteKeys);
    if (primaryOutOfStock || alternateProduct) {
      html += renderAlternateTile(alternateProduct, alternateSkuKey);
    }
    html += renderCrossSellStrip(crossSellProducts);
    html += '<div class="tss-quiz__restart"><button type="button" class="tss-btn tss-btn--outline" data-action="restart">Retake Quiz</button></div>';
    html += '</div>';

    app.querySelector('.tss-quiz__step').innerHTML = html;
    bindResultEvents();

    try {
      if (!sessionStorage.getItem('tss_quiz_completed')) {
        sessionStorage.setItem('tss_quiz_completed', 'true');
        if (typeof window.fbq !== 'undefined') {
          window.fbq('trackCustom', 'QuizComplete');
        }
      }
    } catch (e) {}
  };

  /* ══════════════════════════════════════════
     EVENT BINDING
     ══════════════════════════════════════════ */
  const bindEvents = () => {
    const startBtn = app.querySelector('[data-action="start-quiz"]');
    if (startBtn) {
      startBtn.addEventListener('click', () => {
        const cinematicIntro = app.querySelector('.tss-quiz__cinematic');
        if (cinematicIntro) {
          cinematicIntro.classList.add('tss-quiz__cinematic-exit');
          setTimeout(() => transitionTo(0), 600);
        } else {
          transitionTo(0);
        }
      });
    }

    app.querySelectorAll('[data-purpose]').forEach((btn) => {
      btn.addEventListener('click', () => {
        purpose = btn.dataset.purpose;
        app.querySelectorAll('[data-purpose]').forEach((o) => {
          o.classList.remove('is-selected');
          const chk = o.querySelector('.tss-quiz__option-check');
          if (chk) chk.remove();
        });
        btn.classList.add('is-selected');
        const cs = document.createElement('span');
        cs.className = 'tss-quiz__option-check';
        cs.innerHTML = svgCheck;
        btn.appendChild(cs);
        setTimeout(() => transitionTo(1), 400);
      });
    });

    app.querySelectorAll('.tss-quiz__option[data-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const qIndex = step - 1;
        answers[qIndex] = btn.dataset.value;
        app.querySelectorAll('.tss-quiz__option[data-value]').forEach((o) => {
          o.classList.remove('is-selected');
          const chk = o.querySelector('.tss-quiz__option-check');
          if (chk) chk.remove();
        });
        btn.classList.add('is-selected');
        const checkSpan = document.createElement('span');
        checkSpan.className = 'tss-quiz__option-check';
        checkSpan.innerHTML = svgCheck;
        btn.appendChild(checkSpan);
        const nextBtn = app.querySelector('[data-action="next"]');
        if (nextBtn) nextBtn.disabled = false;
        setTimeout(() => {
          if (answers[qIndex] === btn.dataset.value) transitionTo(step + 1);
        }, 500);
      });
    });

    const nextBtn = app.querySelector('[data-action="next"]');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const qIndex = step - 1;
        if (!answers[qIndex]) return;
        transitionTo(step + 1);
      });
    }

    app.querySelectorAll('[data-action="back"]').forEach((btn) => {
      btn.addEventListener('click', () => transitionTo(step - 1));
    });

    app.querySelectorAll('[data-jump-to]').forEach((node) => {
      node.addEventListener('click', () => {
        const targetStep = parseInt(node.dataset.jumpTo, 10);
        if (!isNaN(targetStep) && targetStep < step) transitionTo(targetStep);
      });
    });

    const submitBtn = app.querySelector('[data-action="submit"]');
    const phoneInput = document.getElementById('tss-quiz-phone');
    const phoneError = document.getElementById('tss-quiz-phone-error');

    const attemptSubmit = () => {
      const value = phoneInput ? phoneInput.value.trim() : '';
      if (!isValidIndianPhone(value)) {
        if (phoneInput) phoneInput.classList.add('has-error');
        if (phoneError) {
          phoneError.textContent = "That number doesn't look right \u2014 try again, or skip this step.";
          phoneError.hidden = false;
        }
        if (phoneInput) phoneInput.focus();
        return;
      }
      capturedPhone = value;
      /* Real capture, not a fallback: notifies the store via Shopify's native contact
         form email. Also kept in sessionStorage + dispatched as a custom event so a
         future WhatsApp integration (BiteSpeed etc) has something to hook onto. */
      submitPhoneToShopifyContact(capturedPhone);
      try { sessionStorage.setItem('tss_quiz_phone', capturedPhone); } catch (e) {}
      document.dispatchEvent(new CustomEvent('tss:quiz:phone-captured', { detail: { phone: capturedPhone } }));
      transitionTo(step + 1);
    };

    if (submitBtn) submitBtn.addEventListener('click', attemptSubmit);

    if (phoneInput) {
      phoneInput.addEventListener('input', () => {
        phoneInput.classList.remove('has-error');
        if (phoneError) phoneError.hidden = true;
      });
      phoneInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          attemptSubmit();
        }
      });
    }

    const skipBtn = app.querySelector('[data-action="skip"]');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        capturedPhone = null;
        transitionTo(step + 1);
      });
    }
  };

  const bindResultEvents = () => {
    /* Native gallery init, when the primary card has multiple images */
    const trackEl = app.querySelector('.tss-quiz-gallery-track');
    const dotsContainer = app.querySelector('.tss-quiz-gallery-dots');
    const prevBtn = app.querySelector('.tss-quiz-gallery-arrow.prev');
    const nextBtn = app.querySelector('.tss-quiz-gallery-arrow.next');
    const thumbSlides = app.querySelectorAll('.tss-quiz-thumb-slide');

    if (trackEl) {
      const slides = trackEl.querySelectorAll('.tss-quiz-gallery-slide');
      
      if (dotsContainer) {
        dotsContainer.innerHTML = '';
        slides.forEach((_, i) => {
          const dot = document.createElement('div');
          dot.className = 'tss-quiz-gallery-dot' + (i === 0 ? ' is-active' : '');
          dot.addEventListener('click', () => goToSlide(i));
          dotsContainer.appendChild(dot);
        });
      }
      
      const dots = app.querySelectorAll('.tss-quiz-gallery-dot');
      
      const goToSlide = (index) => {
        if (index < 0 || index >= slides.length) return;
        const slideWidth = trackEl.clientWidth;
        trackEl.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
        updateActiveState(index);
      };
      
      const updateActiveState = (index) => {
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        thumbSlides.forEach((thumb, i) => {
          thumb.style.borderColor = i === index ? 'var(--section-text, #1b3d58)' : 'transparent';
          if (i === index) {
            const thumbTrack = thumb.parentElement;
            if (thumbTrack) {
              thumbTrack.scrollTo({
                left: thumb.offsetLeft - thumbTrack.clientWidth / 2 + thumb.clientWidth / 2,
                behavior: 'smooth'
              });
            }
          }
        });
      };
      
      if (prevBtn) prevBtn.addEventListener('click', () => {
        const index = Math.round(trackEl.scrollLeft / trackEl.clientWidth);
        goToSlide(Math.max(0, index - 1));
      });
      if (nextBtn) nextBtn.addEventListener('click', () => {
        const index = Math.round(trackEl.scrollLeft / trackEl.clientWidth);
        goToSlide(Math.min(slides.length - 1, index + 1));
      });
      
      thumbSlides.forEach((thumb, i) => {
        thumb.addEventListener('click', () => goToSlide(i));
      });
      
      let isScrolling;
      trackEl.addEventListener('scroll', () => {
        window.clearTimeout(isScrolling);
        isScrolling = setTimeout(() => {
          const index = Math.round(trackEl.scrollLeft / trackEl.clientWidth);
          updateActiveState(index);
        }, 60);
      }, { passive: true });
    }

    /* Quantity stepper on the primary card */
    const qtyInput = app.querySelector('[data-qty-input]');
    app.querySelectorAll('.tss-quiz__qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!qtyInput) return;
        let current = parseInt(qtyInput.value, 10) || 1;
        if (btn.dataset.action === 'plus') current++;
        else if (btn.dataset.action === 'minus' && current > 1) current--;
        qtyInput.value = current;
      });
    });

    /* Inline add-on checkboxes — reflect the running total next to Add to Cart */
    const updateAddonTotal = (card) => {
      const totalEl = card.querySelector('[data-addon-total]');
      const atcBtn = card.querySelector('.tss-quiz__showcase-atc[data-base-price]');
      if (!totalEl || !atcBtn) return;
      const checked = Array.from(card.querySelectorAll('.tss-quiz__addon-checkbox:checked'));
      if (!checked.length) {
        totalEl.hidden = true;
        return;
      }
      const addonSum = checked.reduce((sum, cb) => sum + parseInt(cb.dataset.price, 10), 0);
      const basePrice = parseInt(atcBtn.dataset.basePrice, 10);
      totalEl.textContent = 'Total: ' + formatPrice(basePrice + addonSum);
      totalEl.hidden = false;
    };
    app.querySelectorAll('.tss-quiz__addon-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        const card = cb.closest('.tss-quiz__hero-showcase');
        if (card) updateAddonTotal(card);
      });
    });

    /* Live variant switching on the primary card, when it has real options */
    const showcase = app.querySelector('.tss-quiz__hero-showcase[data-variants]');
    if (showcase) {
      const variantsData = JSON.parse(showcase.dataset.variants || '[]');
      const selects = showcase.querySelectorAll('.tss-quiz__option-select');
      const priceEl = showcase.querySelector('[data-price-display]');
      const compareEl = showcase.querySelector('[data-compare-display]');
      const atcBtn = showcase.querySelector('.tss-quiz__showcase-atc[data-variant-id]');

      selects.forEach((select) => {
        select.addEventListener('change', () => {
          const selectedOptions = Array.from(selects).map((s) => s.value);
          const matchingVariant = variantsData.find((v) => {
            return selectedOptions.every((val, index) => v['option' + (index + 1)] === val);
          });
          if (matchingVariant && atcBtn) {
            atcBtn.dataset.variantId = matchingVariant.id;
            atcBtn.dataset.basePrice = matchingVariant.price;
            atcBtn.disabled = matchingVariant.available === false;
            atcBtn.textContent = matchingVariant.available === false ? 'SOLD OUT' : 'ADD TO CART';
            updateAddonTotal(showcase);
            if (priceEl) priceEl.textContent = formatPrice(matchingVariant.price);
            if (compareEl) {
              if (matchingVariant.compare_at_price > matchingVariant.price) {
                compareEl.textContent = formatPrice(matchingVariant.compare_at_price);
                compareEl.style.display = 'inline-block';
              } else {
                compareEl.style.display = 'none';
              }
            }
          }
        });
      });
    }

    app.querySelectorAll('[data-action="restart-to-q1"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        step = 0;
        answers.length = 0;
        syncUrl();
        render();
      });
    });

    app.querySelectorAll('.tss-quiz__showcase-atc[data-variant-id]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        const card = btn.closest('.tss-quiz__hero-showcase');
        const items = [{ id: parseInt(variantId, 10), quantity: qty }];
        if (card) {
          card.querySelectorAll('.tss-quiz__addon-checkbox:checked').forEach((cb) => {
            items.push({ id: parseInt(cb.dataset.variantId, 10), quantity: 1 });
          });
        }
        btn.disabled = true;
        btn.textContent = 'Adding\u2026';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          });
          btn.textContent = res.ok ? 'Added!' : 'Error \u2014 try again';
          if (res.ok) {
            handleFloCartBtn();
            document.documentElement.dispatchEvent(new CustomEvent('theme:cart:updated'));
          }
        } catch { btn.textContent = 'Error \u2014 try again'; }
        setTimeout(() => { btn.textContent = 'ADD TO CART'; btn.disabled = false; }, 2000);
      });
    });

    app.querySelectorAll('.tss-quiz__showcase-atc[data-bundle-variant-ids]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const ids = btn.dataset.bundleVariantIds.split(',').map((id) => ({ id: parseInt(id, 10), quantity: 1 }));
        btn.disabled = true;
        btn.textContent = 'Adding\u2026';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: ids })
          });
          btn.textContent = res.ok ? 'Added!' : 'Error \u2014 try again';
          if (res.ok) {
            handleFloCartBtn();
            document.documentElement.dispatchEvent(new CustomEvent('theme:cart:updated'));
          }
        } catch { btn.textContent = 'Error \u2014 try again'; }
        setTimeout(() => { btn.textContent = 'ADD BOTH TO CART'; btn.disabled = false; }, 2000);
      });
    });

    bindRestartEvents();
  };

  const bindRestartEvents = () => {
    app.querySelectorAll('[data-action="restart"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        step = -1;
        purpose = null;
        answers.length = 0;
        window.history.replaceState({}, '', window.location.pathname);
        render();
      });
    });
  };

  /* ── Boot ───────────────────────────────── */
  init();
});
